import React, { useRef } from 'react';

/**
 * 6-digit OTP input as separate boxes — auto-advance, backspace to previous,
 * and full-code paste support. Calls onChange with the joined string.
 */
function OtpInput({ value = '', onChange, length = 6, autoFocus = true }) {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const commit = (next) => onChange(next.join('').slice(0, length));

  const handleChange = (i, ch) => {
    const c = ch.replace(/\D/g, '');
    if (!c) return;
    const next = [...digits];
    // Support typing/pasting multiple digits at once
    const chars = c.split('');
    let pos = i;
    for (const d of chars) {
      if (pos >= length) break;
      next[pos] = d;
      pos += 1;
    }
    commit(next);
    refs.current[Math.min(pos, length - 1)]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[i]) {
        next[i] = '';
        commit(next);
      } else if (i > 0) {
        next[i - 1] = '';
        commit(next);
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    onChange(text);
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <div className="flex justify-between gap-2" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          inputMode="numeric"
          maxLength={1}
          value={d}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className="w-11 h-12 text-center text-xl font-mono font-bold bg-field border border-field-border rounded-[10px] text-fg focus:border-orange transition-colors"
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

export default OtpInput;
